import routes from '@routes';
import store from '@state/store';
import {
  AUTH,
  CITA,
  CONTRACT_SEARCH,
  CONTRACT_SEARCH_REDIRECT,
  ERROR,
  ERROR_FATAL_CODE,
  ERROR_MEDIUM_CODE,
  HOME,
  LOGIN,
  LOGOUT,
  PASSWORD,
  UNBARRING,
} from '@/router/route-types';

let cnt = 0;

export default async (to, from, next) => {
  const originalNext = next;
  next = (obj) => {
    cnt++;

    if (obj === undefined) {
      cnt = 0;
      return originalNext();
    }

    if (cnt > 100) throw new Error('beforeEach - LOOP DETECTED!');
    else originalNext(obj);
  };

  try {
    await store.state.system.initPromise;
  } catch (e) {
    if (to.matched[0].name !== ERROR) {
      next({ name: ERROR, params: { error: e } });
      return;
    }
  }

  if (from?.matched?.[0]?.name === ERROR && to?.matched?.[0]?.name !== ERROR && !store.getters['system/isProduction']) {
    // eslint-disable-next-line no-console
    console.warn('navigating away from error', from.params?.error);
  }

  if ([ERROR, LOGIN, LOGOUT].includes(to.matched[0].name)) {
    next();
    return;
  }

  if (!store) {
    next({ name: ERROR, params: { error: 'VUEX STORE NOT FOUND' } });
    return;
  }

  if (store.state.system.isAuthExpired && !store.getters['system/wasAuthenticated']) {
    await store.dispatch('system/reInit'); // for anonymous session
  }

  const isAuthenticated = store.getters['system/isAuthenticated'];

  /* get all rights of this route and its ancestors */
  const rights = [...new Set([].concat(...to.matched.filter((m) => m.meta && m.meta.rights).map((m) => m.meta.rights)))];

  if (!isAuthenticated && store.getters['system/isPageContextAuth'] && !to.matched.some((m) => m.meta.isAuthPage)) {
    next({
      name: AUTH,
    });
  } else if (!isAuthenticated && store.getters['system/isPageContextCita'] && !to.matched.some((m) => m.meta.isCitaPage)) {
    next({
      name: CITA,
    });
  } else if (!isAuthenticated && store.getters['system/isPageContextUnbarring'] && !to.matched.some((m) => m.meta.isUnbarringPage)) {
    next({
      name: UNBARRING,
    });
  } else if (to.matched.some((m) => m.meta.isAuthPage || m.meta.isCitaPage) && isAuthenticated) {
    /*
     * If route to a auth page, but is already authenticated
     */
    next({
      name: HOME,
    });
  } else if (to.matched.some((m) => m.meta.isAuthPage) && !store.getters['system/isPageContextAuth']) {
    next({
      name: ERROR_FATAL_CODE,
      params: {
        code: 'NO_PERMISSION',
      },
    });
  } else if (to.matched.some((m) => m.meta.isCitaPage) && !store.getters['system/isPageContextCita']) {
    next({
      name: ERROR_FATAL_CODE,
      params: {
        code: 'NO_PERMISSION',
      },
    });
  } else if (store.getters['system/isPageContextUnbarring'] && !to.matched.some((m) => m.meta.isUnbarringPage) && !to.matched.some((m) => m.meta.isErrorPage)) {
    next({
      name: ERROR_FATAL_CODE,
      params: {
        code: 'NO_PERMISSION',
      },
    });
  } else if (to.matched.some((m) => m.meta.isAuthRequired) && !isAuthenticated) {
    /*
     * If the user is not authenticated and visits
     * a page that requires authentication, redirect to the login page
     * if we logged in from there, otherwise redirect to error page
     */
    if (store.getters['system/isOldAuthTypeLogin']) {
      next({
        name: LOGIN,
        params: {
          originalToName: to.name,
          originalToParams: to.params,
        },
      });
    } else {
      next({
        name: ERROR_FATAL_CODE,
        params: {
          code: 'EXPIRED_SESSION',
        },
      });
    }
  } else if (to.matched.some((m) => m.meta.isNotAuthRequired) && isAuthenticated) {
    /*
     * If the user is authenticated and visits
     * an isNotAuthRequired page, redirect to the dashboard page
     */
    next({
      name: HOME,
    });
  } else if (rights.some((r) => !store.getters['system/hasRight'](r))) {
    /*
     * If the target navigation has the meta attribute "rights",
     * verify that the corresponding rights are TRUE.
     */
    next({
      name: HOME,
    });
  } else if (to.matched.some((m) => m.meta.isContractRequired) && !store.state.contract.contractHash) {
    /*
     * If the user has to select a contract first, redirect to form
     */
    next({
      name: CONTRACT_SEARCH_REDIRECT,
      params: {
        errorSelectContract: true,
        originalToName: to.name,
        originalToParams: to.params,
      },
    });
  } else if (
    store.getters['system/isAuthTypeOpenId'] &&
    !store.state.contract.contractHash &&
    ![CONTRACT_SEARCH, CONTRACT_SEARCH_REDIRECT].includes(to.name) &&
    store.getters['system/isContractSearchAllowed'] &&
    from?.name == null
  ) {
    /*
     * If we logged in with OpenId und have no contract selected prompt user to select one
     * This is done to mimic the behavior of the "Kundencenter"
     */
    next({
      name: CONTRACT_SEARCH_REDIRECT,
      params: {
        errorSelectContract: false,
        originalToName: to.name,
        originalToParams: to.params,
      },
    });
  } else if (isAuthenticated && to.name === PASSWORD && !store.getters['system/showPasswordChangeForm']) {
    next({
      name: ERROR_MEDIUM_CODE,
      params: {
        code: 'PASSWORD_CHANGE_DISABLED',
      },
    });
  } else if (!to.matched.length) {
    next({
      name: ERROR_FATAL_CODE,
      params: {
        code: 'ROUTE_NOT_FOUND',
      },
    });
  } else {
    const nextName = await store.dispatch('system/redirect');
    let actualName = to.matched[0].name;
    let defaultName = null;
    // in case of a nested route with default, we don't have a name property on the parent!
    if (!actualName && to.matched.length > 1) {
      // also see Navigation.vue
      actualName = to.matched[1].name;
      defaultName = routes
        .find((m) => m.path === to.matched[0].path) // find root route
        .children.find((m) => m.path === '').name; // and get default route name
    }

    /**
     * Allow contract selection before forwarding to deep link target.
     */
    if (nextName && to.params.originalToName) {
      to.params.originalToName = nextName;
    } else if (nextName && !to.matched.some((m) => m.name === nextName) && defaultName !== nextName) {
      /**
       * Forward only if the target (nextName) is not one of the current route's names
       * or has a different name than the current route's default name
       * (which is the children with an empty path)
       */
      next({ name: nextName, params: to.params });
      return;
    }
    next();
  }
};
